import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import ClientsMarquee from "@/components/ClientsMarquee";
import GradientStrip from "@/components/GradientStrip";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG, api } from "@/lib/config";
import { useT } from "@/lib/i18n";

export default function About() {
  const t = useT();
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
  }, []);

  const KEY_FACTS = t("about.keyFacts", []) || [];
  const INTERSECTIONS = t("about.intersections.items", []) || [];
  const CERTS = t("about.certifications.items", []) || [];

  return (
    <Layout headerVariant="dark">
      <SEO
        title="À propos · William Marques, fondateur"
        description="Avant le coaching, l'opérationnel. 12 ans en direction des opérations et en CODIR. 70+ collaborateurs accompagnés en croissance. 2 000+ projets pilotés pour des clients Top 40 et firmes internationales."
        path="/a-propos"
      />
      <section data-testid="about-hero" className="relative min-h-[80svh] grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.codir} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-deep/90 via-brand/70 to-ink/85" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-52 pb-24">
          <p className="section-num text-white/85 fade-up">{t("about.eyebrow")}</p>
          <h1 className="display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl fade-up-2">
            {t("about.titleA")}<em className="not-italic text-accent-pink">{t("about.titleAccent")}</em>{t("about.titleB")}
          </h1>
          <p className="text-white/85 text-lg md:text-xl leading-relaxed mt-10 max-w-3xl fade-up-3">
            {t("about.lede")}
          </p>
        </div>
        <GradientStrip />
      </section>

      <section data-testid="about-keyfacts" className="bg-paper py-20 md:py-28 border-b border-ink/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {KEY_FACTS.map((f, i) => (
            <div key={i} className="border-l border-ink/15 pl-5">
              <p className="display text-brand text-4xl md:text-5xl">{f.num}</p>
              <p className="text-[13px] uppercase tracking-wider text-ink/60 mt-3 leading-snug">{f.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-paper py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="section-num">{t("about.bio.eyebrow")}</p>
            <h2 className="display text-4xl md:text-5xl leading-tight mt-6">
              {t("about.bio.title")}<em className="not-italic text-brand">{t("about.bio.titleAccent")}</em>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-5 text-[16px] md:text-[17px] leading-relaxed text-ink/85">
            <p>{t("about.bio.p1")}</p>
            <p>{t("about.bio.p2")}</p>
            <p>{t("about.bio.p3")}</p>
            <p>{t("about.bio.p4")}</p>
            <p className="border-l-2 border-brand pl-5 italic text-ink/75">{t("about.bio.p5")}</p>
          </div>
        </div>
      </section>

      <section className="bg-brand-deep text-white py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="section-num text-white/85">{t("about.intersections.eyebrow")}</p>
          <h2 className="display text-4xl md:text-6xl leading-tight mt-6 max-w-4xl">
            {t("about.intersections.title")}
          </h2>
          <p className="text-white/90 text-lg leading-relaxed mt-10 max-w-2xl">{t("about.intersections.lede")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-4 mt-12">
            {INTERSECTIONS.map((it, i) => (
              <div key={i} className="border-t border-white/15 py-5 flex items-center gap-5">
                <span className="display text-accent-yellow text-sm tracking-[0.3em]">0{i + 1}</span>
                <span className="display text-2xl">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper-2 py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="section-num">{t("about.certifications.eyebrow")}</p>
            <h2 className="display text-4xl md:text-5xl leading-tight mt-6">
              {t("about.certifications.title")}<em className="not-italic text-brand">{t("about.certifications.titleAccent")}</em>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-[16px] leading-relaxed text-ink/85">
            {CERTS.map((c, i) => (
              <div key={i} className="border-t border-ink/15 pt-5">
                <p className="display text-lg">{c.label}</p>
                <p className="text-ink/75 mt-1">{c.org}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section data-testid="about-testimonials" className="bg-paper py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="section-num">{t("about.testimonials.eyebrow")}</p>
            <h2 className="display text-4xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("about.testimonials.title")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 mt-16">
              {testimonials.map((tt) => (
                <div key={tt.id} className="bg-paper p-10 md:p-12 flex flex-col justify-between min-h-[360px]">
                  <p className="display text-2xl text-brand leading-tight">"</p>
                  <p className="text-[15px] leading-relaxed text-ink/85 mt-4 flex-1">{tt.quote}</p>
                  <div className="mt-8 pt-6 border-t border-ink/15">
                    <p className="display text-lg">{tt.author}</p>
                    <p className="text-[13px] text-ink/70 mt-1">{tt.role}{tt.company && tt.company !== "" ? ` · ${tt.company}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ClientsMarquee />

      <section className="bg-brand text-white py-24 md:py-32 grain relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="section-num text-white/85">{t("about.cta.eyebrow")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("about.cta.title")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="about-cta" className="btn-pill bg-white text-brand hover:bg-paper-2">
              {t("common.cta.bookCall")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
